// Practice-only module settings. The gear on a module's setup screen lets
// someone change how long a practice run lasts, how many questions it has
// and, for a few modules, how long things stay on screen. They are kept per
// module and per difficulty, and only ever change Practice: assessments,
// mock tests and every recorded run keep the standard settings.

export const PRACTICE_DURATION_LIMITS = { min: 30, max: 60 * 60 };
export const PRACTICE_QUESTION_LIMIT = 60;
export const PRACTICE_DIFFICULTIES = ['easy', 'medium', 'hard'];

const questionsAndTime = ({ maxQuestions = PRACTICE_QUESTION_LIMIT, extras = [] } = {}) => ({
  questions: true,
  maxQuestions,
  extras
});
const timeOnly = () => ({ questions: false, maxQuestions: 0, extras: [] });

// What each module's gear offers. A module that isn't listed has no gear:
// Dynamic Projection is one scripted scenario of a set length, Dynamic Pilot
// Workload runs a fixed schedule, and Flight Data Memory doesn't use these
// settings at all. A module drawing on a fixed bank of questions can't offer
// more than the bank holds.
export const PRACTICE_SETTINGS_FIELDS = {
  abstractReasoning: questionsAndTime(),
  airborneNumerical: questionsAndTime(),
  anglesBearingsDegrees: questionsAndTime(),
  auditoryCapacity: timeOnly(),
  aviationMaths: questionsAndTime(),
  cognitiveUpdating: timeOnly(),
  coloursLettersNumbers: timeOnly(),
  controlStabilization: timeOnly(),
  digitRecognition: questionsAndTime({
    extras: [
      { key: 'digitCount', label: 'Digits per sequence', unit: 'digits', min: 4, max: 15, step: 1 },
      { key: 'showTime', label: 'Digits on screen for', unit: 'sec', min: 0.5, max: 10, step: 0.5 }
    ]
  }),
  directionsDistances: questionsAndTime(),
  figuresLogisticsGroups: timeOnly(),
  flightPathTracking: timeOnly(),
  instrumentComprehension: questionsAndTime(),
  mathematicsReasoning: questionsAndTime(),
  mechanicalReasoning: questionsAndTime(),
  mentalRotation: questionsAndTime(),
  numericalOperations: questionsAndTime(),
  numericalReasoning: questionsAndTime(),
  perceptualSpeedAccuracy: questionsAndTime(),
  physicsForPilotSelection: questionsAndTime({ maxQuestions: 38 }),
  rapidTracking: timeOnly(),
  rapidTracking2: timeOnly(),
  sensoryMotor: timeOnly(),
  situationalAwareness: questionsAndTime(),
  spatialIntegration: questionsAndTime({
    extras: [
      { key: 'studyDuration', label: 'Study time per round', unit: 'sec', min: 5, max: 60, step: 1 }
    ]
  }),
  spatialOrientation: questionsAndTime(),
  systemLogic: questionsAndTime({ maxQuestions: 32 }),
  tableReading: questionsAndTime(),
  targetRecognition: timeOnly(),
  taskManager: timeOnly(),
  traceTest: questionsAndTime(),
  traceTest2: questionsAndTime({
    maxQuestions: 30,
    extras: [
      { key: 'animationDuration', label: 'Watch time per scenario', unit: 'sec', min: 3, max: 15, step: 0.5 }
    ]
  }),
  verbalLogic: questionsAndTime({ maxQuestions: 35 }),
  verbalReasoning: questionsAndTime(),
  vigilance: timeOnly(),
  visualSearch: questionsAndTime(),
  visualisationTests: questionsAndTime()
};

export const getPracticeSettingsFields = (settingsKey) => PRACTICE_SETTINGS_FIELDS[settingsKey] || null;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const roundToStep = (value, step) => Math.round(value / step) * step;

// Keeps only the values this module's gear offers, each within its limits.
// Returns null when nothing valid is left.
export const sanitizePracticeValues = (settingsKey, values) => {
  const fields = getPracticeSettingsFields(settingsKey);
  if (!fields || !values || typeof values !== 'object') return null;
  const clean = {};
  const duration = Number(values.testDuration);
  if (Number.isFinite(duration)) {
    clean.testDuration = clamp(Math.round(duration), PRACTICE_DURATION_LIMITS.min, PRACTICE_DURATION_LIMITS.max);
  }
  const questions = Number(values.questionCount);
  if (fields.questions && Number.isFinite(questions)) {
    clean.questionCount = clamp(Math.round(questions), 1, fields.maxQuestions);
  }
  fields.extras.forEach(({ key, min, max, step }) => {
    const value = Number(values[key]);
    if (Number.isFinite(value)) clean[key] = clamp(roundToStep(value, step), min, max);
  });
  return Object.keys(clean).length ? clean : null;
};

// The whole stored object, cleaned: { [settingsKey]: { [difficulty]: values } }.
export const sanitizePracticeSettings = (stored) => {
  if (!stored || typeof stored !== 'object') return {};
  const clean = {};
  Object.entries(stored).forEach(([settingsKey, levels]) => {
    if (!getPracticeSettingsFields(settingsKey) || !levels || typeof levels !== 'object') return;
    PRACTICE_DIFFICULTIES.forEach((level) => {
      const values = sanitizePracticeValues(settingsKey, levels[level]);
      if (!values) return;
      clean[settingsKey] = { ...(clean[settingsKey] || {}), [level]: values };
    });
  });
  return clean;
};

// A module's settings for one difficulty, with that difficulty's practice
// changes on top. getSettings() hangs each module's practice changes on its
// settings as a hidden `practice` property, so they never reach a saved copy
// of the settings or an assessment.
export const getPracticeConfig = (difficultySettings, difficulty) => {
  const standard = difficultySettings?.[difficulty];
  const custom = difficultySettings?.practice?.[difficulty];
  return standard && custom ? { ...standard, ...custom } : standard;
};

export const hasPracticeChanges = (difficultySettings, difficulty) => {
  const standard = difficultySettings?.[difficulty];
  const custom = difficultySettings?.practice?.[difficulty];
  return Boolean(standard && custom && Object.entries(custom).some(([key, value]) => standard[key] !== value));
};
